import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sidebar } from '../layout/Sidebar';
import { Navigation } from '../layout/Navigation';
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageSquare, Trash2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BloombergRadioButton } from '../trading/BloombergRadioButton';
import { useChatHistory } from '@/hooks/useChatHistory';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from '@/store';
import { EmptyChatHistory } from './EmptyChatHistory';
import { Card } from '../ui/card';
import { ChatInterface } from './ChatInterface';
import { ScrollAreaWithChildren } from '@/components/ui/scroll-area-with-children';
import { generateChatResponse } from '@/services/llmService';
import { detectFinancialContext, enhancedFinancialResponse } from '@/services/chatService';
import { v4 as uuidv4 } from 'uuid';

export const ChatHistory = () => {
  const navigate = useNavigate();
  const { conversations, isLoading, refreshHistory } = useChatHistory();
  const { toast } = useToast();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [historyMessages, setHistoryMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoadingInput, setIsLoadingInput] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedConversation) {
      setHistoryMessages([]);
      return;
    }
    (async () => {
      try {
        const { data: chatData, error: chatError } = await supabase
          .from('chat_messages')
          .select('id, sender, content, timestamp')
          .eq('conversation_id', selectedConversation)
          .order('timestamp', { ascending: true });
        if (chatError) throw chatError;
        const loaded: ChatMessage[] = (chatData || []).map((msg: any) => ({
          id: msg.id,
          role: msg.sender as 'user' | 'assistant',
          content: msg.content,
          timestamp: new Date(msg.timestamp),
        }));
        setHistoryMessages(loaded);
      } catch (e) {
        console.error('Error loading conversation messages:', e);
      }
    })();
  }, [selectedConversation]);
  
  const handleBackNavigation = () => {
    const lastActiveView = localStorage.getItem('lastActiveView') || 'chat';
    navigate('/dashboard', { state: { activeView: lastActiveView } });
  };
  
  const handleDeleteConversation = async (conversationId: string) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    try {
      const { error: msgError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', conversationId);
      if (msgError) throw msgError;
      const { error: convError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);
      if (convError) throw convError;
      if (selectedConversation === conversationId) {
        setSelectedConversation(null);
      }
      refreshHistory();
      toast({
        title: 'Conversation Deleted',
        description: 'The conversation was successfully deleted.',
      });
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      toast({
        title: 'Error',
        description: `Failed to delete conversation: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  // Function to generate a meaningful conversation summary
  const generateConversationSummary = (title: string, lastMessage: string | null) => {
    if (!lastMessage) {
      return "Financial discussion with Stewie AI";
    }
    
    // Check if the title already contains a good summary
    if (title && title.length > 10 && !title.toLowerCase().includes('conversation') && !title.toLowerCase().includes('chat')) {
      return `Discussion about ${title.toLowerCase()}`;
    }
    
    // Create a summary based on common financial topics
    const message = lastMessage.toLowerCase();
    if (message.includes('portfolio') || message.includes('investment')) {
      return "Portfolio analysis and investment strategies";
    } else if (message.includes('market') || message.includes('stock') || message.includes('trading')) {
      return "Market analysis and trading insights";
    } else if (message.includes('sector') || message.includes('industry')) {
      return "Sector and industry analysis";
    } else if (message.includes('risk') || message.includes('volatility')) {
      return "Risk management and volatility analysis";
    } else if (message.includes('earnings') || message.includes('financial')) {
      return "Financial performance and earnings analysis";
    } else if (message.includes('strategy') || message.includes('plan')) {
      return "Investment strategy and planning";
    } else {
      // Fallback: truncate and clean up the message
      const cleaned = lastMessage.replace(/^\w+:\s*/, ''); // Remove "user:" prefix if exists
      const truncated = cleaned.length > 50 ? cleaned.substring(0, 50) + '...' : cleaned;
      return truncated;
    }
  };

  // Helper function to save user message to database
  const saveUserMessage = async (conversationId: string, content: string, userId: string) => {
    await supabase.from('chat_messages').insert({ 
      conversation_id: conversationId, 
      sender: 'user', 
      content,
      user_id: userId
    });
  };

  // Helper function to generate AI response
  const generateAIResponse = async (currentInput: string, historyForAPI: Array<{ role: string; content: string }>) => {
    const { isFinancial, symbols } = detectFinancialContext(currentInput);
    
    let aiContent = '';
    let aiMetadata = null;
    
    // If financial content is detected and we have symbols, use the financial LLM
    if (isFinancial && symbols.length > 0) {
      try {
        console.log('Using financial LLM with symbols:', symbols.join(', '));
        const financialResponse = await enhancedFinancialResponse(currentInput, symbols);
        
        if (financialResponse.success && financialResponse.response) {
          aiContent = financialResponse.response;
          aiMetadata = {
            model_used: financialResponse.model_used ?? 'Financial LLM',
            rag_sources_used: financialResponse.rag_sources_used ?? ['Alpaca market data']
          };
        } else {
          // If financial LLM fails, fall back to standard LLM
          console.log('Financial LLM failed, falling back to standard LLM');
          aiContent = await generateChatResponse(currentInput, historyForAPI);
        }
      } catch (finError) {
        console.error('Error using financial LLM:', finError);
        aiContent = await generateChatResponse(currentInput, historyForAPI);
      }
    } else {
      // Use standard chat response for non-financial queries
      aiContent = await generateChatResponse(currentInput, historyForAPI);
    }

    return { aiContent, aiMetadata };
  };

  // Helper function to save AI message to database
  const saveAIMessage = async (conversationId: string, content: string, userId: string, metadata: any) => {
    await supabase.from('chat_messages').insert({ 
      conversation_id: conversationId, 
      sender: 'assistant', 
      content,
      user_id: userId,
      metadata
    });
  };

  // Helper function to update conversation metadata
  const updateConversationMetadata = async (conversationId: string, currentInput: string) => {
    // Only update title if it's generic or missing
    const currentConversation = conversations.find(c => c.id === conversationId);
    const shouldUpdateTitle = !currentConversation?.title || 
      currentConversation.title.toLowerCase().includes('new conversation') ||
      currentConversation.title.toLowerCase().includes('untitled') ||
      currentConversation.title.toLowerCase().includes('chat');

    await supabase
      .from('conversations')
      .update({ 
        updated_at: new Date().toISOString(),
        ...(shouldUpdateTitle && { 
          title: currentInput.length > 50 ? currentInput.substring(0, 50) + '...' : currentInput 
        })
      })
      .eq('id', conversationId);
  };

  // Function to handle continuing a conversation from chat history
  const handleContinueConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoadingInput || !selectedConversation) return;
    
    const currentInput = inputMessage;
    setInputMessage('');
    setIsLoadingInput(true);

    // Generate unique IDs for the messages
    const userMessageId = uuidv4();
    const aiMessageId = uuidv4();

    try {
      // Get current user session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session?.user) {
        throw new Error(sessionError?.message || 'User not authenticated');
      }

      // Create user message object
      const userMessage: ChatMessage = {
        id: userMessageId,
        role: 'user',
        content: currentInput,
        timestamp: new Date(),
      };

      // Add user message to local state immediately for better UX
      setHistoryMessages(prev => [...prev, userMessage]);

      // Save user message to database
      await saveUserMessage(selectedConversation, currentInput, sessionData.session.user.id);

      // Prepare history for AI including this new message
      const historyForAPI = [...historyMessages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Generate AI response
      const { aiContent, aiMetadata } = await generateAIResponse(currentInput, historyForAPI);

      // Create AI message object
      const aiMessage: ChatMessage = {
        id: aiMessageId,
        role: 'assistant',
        content: aiContent,
        timestamp: new Date(),
        metadata: aiMetadata
      };

      // Add AI response to local state
      setHistoryMessages(prev => [...prev, aiMessage]);

      // Save AI response to database
      await saveAIMessage(selectedConversation, aiContent, sessionData.session.user.id, aiMetadata);

      // Update conversation's updated_at timestamp and title if needed
      await updateConversationMetadata(selectedConversation, currentInput);

      // Refresh the conversation list so this conversation appears at the top
      refreshHistory();

      toast({
        title: "Message Sent",
        description: "Successfully continued the conversation.",
        variant: "default",
      });

    } catch (error: any) {
      console.error('Error continuing conversation:', error);
      
      // Remove the user message from local state if there was an error
      setHistoryMessages(prev => prev.filter(msg => msg.id !== userMessageId));
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}`,
        timestamp: new Date(),
        error: true,
      };
      setHistoryMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to continue conversation',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingInput(false);
    }
  };

  const renderContent = () => {
    if (selectedConversation) {
      const conversation = conversations.find(c => c.id === selectedConversation);
      if (!conversation) return null;

      return (
        <div className="h-full flex flex-col max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden flex flex-col">
            {/* Enhanced Header with Premium Design matching dashboard chats */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      {conversation.title}
                    </h2>
                    <p className="text-sm text-blue-600 font-medium">Chat History - {format(conversation.updated_at, 'MMM d, yyyy')}</p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  onClick={() => setSelectedConversation(null)}
                  className="bg-white/50 hover:bg-blue-50 border border-blue-200 rounded-lg text-blue-700 hover:text-blue-800 transition-all duration-200"
                >
                  Close
                </Button>
              </div>
            </div>
            
            {/* Enhanced Disclaimer with Premium Styling */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100">
              <div className="p-4 mx-4 my-3 bg-white/70 backdrop-blur-sm rounded-lg border border-blue-200/50">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-blue-100 rounded-md mt-0.5">
                    <Sparkles className="h-3 w-3 text-blue-600" />
                  </div>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    <span className="font-semibold">Historical Conversation:</span> Reviewing past AI financial insights and analysis. 
                    Always validate strategies with your financial advisor.
                  </p>
                </div>
              </div>
            </div>

            {/* Enhanced Chat Interface with increased height */}
            <div className="flex-1 bg-gradient-to-b from-gray-50/30 to-white" style={{ minHeight: '600px' }}>
              <ChatInterface
                messages={historyMessages}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                isLoading={isLoadingInput}
                handleSendMessage={handleContinueConversation}
              />
            </div>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-[400px]">
          <div className="animate-pulse flex flex-col gap-4 w-full max-w-2xl">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl w-full shadow-sm" />
            ))}
          </div>
        </div>
      );
    }

    if (conversations.length === 0) {
      return <EmptyChatHistory />;
    }

    return (
      <ScrollAreaWithChildren className="h-[calc(100vh-160px)]">
        <div className="space-y-4">
          {conversations.map((conversation) => (
            <Card
              key={conversation.id}
              className="p-6 hover:shadow-lg transition-all duration-200 cursor-pointer bg-white border border-gray-200 hover:border-blue-200 rounded-xl"
              onClick={() => setSelectedConversation(conversation.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg mb-1">{conversation.title}</h3>
                    {conversation.last_message && (
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                        {generateConversationSummary(conversation.title, conversation.last_message)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-sm font-medium text-blue-600">
                      {format(conversation.updated_at, 'MMM d')}
                    </span>
                    <div className="text-xs text-gray-500">
                      {format(conversation.updated_at, 'yyyy')}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(conversation.id);
                    }}
                    className="h-8 w-8 rounded-lg hover:bg-red-50 hover:border-red-200 transition-all duration-200"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </ScrollAreaWithChildren>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation 
        activeView="chat"
        setActiveView={() => {}}
        showRoles={false}
        setShowRoles={() => {}}
      />
      <div className="flex">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        
        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackNavigation}
                className="rounded-xl h-10 w-10 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200"
              >
                <ArrowLeft className="h-5 w-5 text-gray-700" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Chat History
                </h1>
                <p className="text-gray-600 font-medium mt-1">Review your past conversations with Stewie AI</p>
              </div>
            </div>
            
            {renderContent()}
          </div>
        </div>
      </div>
      
      <BloombergRadioButton />
    </div>
  );
};
